import { Link } from 'react-router-dom';
import {
  AlertOctagon,
  TrendingUp,
  TrendingDown,
  Zap,
  ChevronRight,
  Activity
} from 'lucide-react';

/**
 * AnomalyAlerts Component
 * Displays detected anomalies in financial data
 */
export default function AnomalyAlerts({ anomalies = [], loading = false }) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="h-20 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Category icons
  const getCategoryIcon = (categoria) => {
    switch (categoria) {
      case 'gastos':
        return TrendingUp;
      case 'ventas':
        return TrendingDown;
      case 'patrones':
        return Activity;
      case 'transacciones':
        return Zap;
      default:
        return AlertOctagon;
    }
  };

  // Type styles
  const getTypeStyle = (tipo) => {
    switch (tipo) {
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600'
        };
      case 'danger':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600'
        };
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-purple-50 rounded-lg flex items-center justify-center border border-purple-200">
            <AlertOctagon className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Deteccion de Anomalias</h2>
            <p className="text-xs text-gray-500">Patrones inusuales detectados</p>
          </div>
        </div>
      </div>

      {/* Anomalies list */}
      {anomalies.length === 0 ? (
        <div className="p-8 text-center">
          <div className="h-12 w-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Activity className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-sm font-medium text-gray-900">Sin anomalias detectadas</p>
          <p className="text-xs text-gray-500">Los patrones financieros son normales</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {anomalies.slice(0, 4).map((anomaly, index) => {
            const style = getTypeStyle(anomaly.tipo);
            const Icon = getCategoryIcon(anomaly.categoria);

            return (
              <div
                key={anomaly.id || index}
                className={`p-4 ${style.bg} border-l-4 ${style.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${style.iconBg}`}>
                    <Icon className={`h-4 w-4 ${style.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900">
                      {anomaly.titulo}
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {anomaly.descripcion}
                    </p>

                    {/* Values comparison */}
                    {anomaly.valor && anomaly.valorReferencia && (
                      <div className="flex items-center gap-4 mt-2">
                        <div className="text-xs">
                          <span className="text-gray-500">Actual: </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(anomaly.valor)}
                          </span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500">Promedio: </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(anomaly.valorReferencia)}
                          </span>
                        </div>
                        {anomaly.porcentaje && (
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                            anomaly.porcentaje > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {anomaly.porcentaje > 0 ? '+' : ''}{anomaly.porcentaje}%
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action link */}
                    {anomaly.accion && (
                      <Link
                        to={anomaly.accion.href}
                        className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        {anomaly.accion.label}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {anomalies.length > 4 && (
        <div className="p-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
          <p className="text-xs text-center text-gray-500">
            {anomalies.length - 4} anomalias adicionales detectadas
          </p>
        </div>
      )}
    </div>
  );
}
